import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';

// Create a context for sound management
const SoundContext = createContext({
  isMuted: false,
  toggleMute: () => { },
  playSound: () => { },
  playWithDelay: () => { },
  stopSound: () => { },
  playSequence: () => { }
});

// Complete sound files mapping - we'll make this more fault-tolerant
const SOUNDS = {
  GAME_START: '/sounds/slot-add-zapsplat.mp3',
  // Regular chess moves
  MOVE: '/sounds/Move_N.mp3',
  CAPTURE: '/sounds/Capture_N.mp3',
  CHECK: '/sounds/check.mp3',
  CHECKMATE: '/sounds/Capture_N.mp3',
  CASTLE: '/sounds/Capture_N.mp3',
  PROMOTION: '/sounds/Capture_N.mp3',

  // Slot machine sounds
  SLOT_SPIN: '/sounds/slot-spin-zapsplat.mp3',
  //SLOT_STOP: '/sounds/slot-stop.mp3',
  SLOT_ADD: '/sounds/slot-add-zapsplat.mp3',
  SLOT_REMOVE: '/sounds/slot-remove-zapslat.mp3',

  // Game result sounds
  WIN: '/sounds/win-zapsplat.mp3',
  LOSE: '/sounds/lose-zapsplat.mp3',
  DRAW: '/sounds/draw-zapsplat.mp3',

  // Countdown and UI sounds
  COUNTDOWN_BEEP: '/sounds/slot-add-zapsplat.mp3',
  COUNTDOWN_FINAL: '/sounds/slot-add-zapsplat.mp3',
  //BUTTON_CLICK: '/sounds/button-click.mp3',
  NOTIFICATION: '/sounds/notification-zapsplat.mp3'
};

// Volume settings for different sound types
const VOLUMES = {
  GAME_START: 0.8,
  // Regular chess moves
  MOVE: 0.9,
  CAPTURE: 1.0,
  CHECK: 1.0,
  CHECKMATE: 1.0,
  CASTLE: 0.9,
  PROMOTION: 0.9,

  // Slot machine sounds
  SLOT_SPIN: 0.2,
  //SLOT_STOP: 0.7,
  SLOT_ADD: 0.6,
  SLOT_REMOVE: 0.6,

  // Game result sounds
  WIN: 1.0,
  LOSE: 0.8,
  DRAW: 0.8,

  // Countdown and UI sounds
  COUNTDOWN_BEEP: 0.7,
  COUNTDOWN_FINAL: 0.8,
  BUTTON_CLICK: 0.5,
  NOTIFICATION: 0.7
};

// Make audio elements globally accessible for debugging
if (typeof window !== 'undefined') {
  window.audioElements = window.audioElements || {};
}

export const SoundProvider = ({ children }) => {
  // Load initial mute state from localStorage
  const [isMuted, setIsMuted] = useState(() => {
    try {
      const savedMute = localStorage.getItem('chess-mute');
      // Default to unmuted (false) if no preference is found
      return savedMute ? JSON.parse(savedMute) : false;
    } catch (error) {
      console.error('Error loading mute preference:', error);
      return false; // Default to unmuted on error
    }
  });

  // Keep track of currently playing sounds (for debugging)
  const [playingSounds, setPlayingSounds] = useState({});

  // Keep track of active sound promises to prevent multiple simultaneous plays
  const activeSoundPromises = useRef({});

  // Rate limiting for sound playback to prevent browser errors
  const soundPlayTimes = useRef({});
  const RATE_LIMIT_MS = 100; // Minimum time between identical sound plays

  // Initialize audio elements
  useEffect(() => {
    // Clean up any existing audio elements
    if (window.audioElements) {
      Object.values(window.audioElements).forEach(audio => {
        if (audio && typeof audio.pause === 'function') {
          try {
            audio.pause();
          } catch (e) {
            // Ignore errors
          }
        }
      });
    }

    // Create fresh audio elements for each sound
    window.audioElements = {};

    // Helper to safely create audio element
    const safeCreateAudio = (key, src) => {
      try {
        const audio = new Audio(src);

        // Configure audio element
        audio.preload = 'auto';
        audio.volume = VOLUMES[key] || 0.8;
        audio.muted = isMuted;

        // Set loop property for SLOT_SPIN
        if (key === 'SLOT_SPIN') {
          audio.loop = true;
        }

        // Add ended event listener to update playing sounds state
        audio.addEventListener('ended', () => {
          setPlayingSounds(prev => ({
            ...prev,
            [key]: false
          }));
        });

        // Store the audio element
        window.audioElements[key] = audio;
      } catch (error) {
        console.error(`Failed to create audio for ${key}:`, error);
      }
    };

    // Create audio elements with error handling
    Object.entries(SOUNDS).forEach(([key, src]) => {
      safeCreateAudio(key, src);
    });

    // Clean up on unmount
    return () => {
      if (window.audioElements) {
        Object.values(window.audioElements).forEach(audio => {
          if (audio && typeof audio.pause === 'function') {
            try {
              audio.pause();

              // Remove event listeners
              audio.removeEventListener('ended', () => { });
            } catch (e) {
              // Ignore errors
            }
          }
        });
      }
    };
  }, [isMuted]); // Only run once on component mount and when mute state changes

  // Update mute state on all audio elements when it changes
  useEffect(() => {
    if (window.audioElements) {
      Object.values(window.audioElements).forEach(audio => {
        if (audio) {
          try {
            audio.muted = isMuted;
          } catch (e) {
            // Ignore errors
          }
        }
      });
    }
    localStorage.setItem('chess-mute', JSON.stringify(isMuted));
  }, [isMuted]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  // Enhanced playSound function with rate limiting and error handling
  const playSound = useCallback((soundName, shouldLoop = false) => {
    if (isMuted) return null;

    // Check for rate limiting
    const now = Date.now();
    const lastPlayTime = soundPlayTimes.current[soundName] || 0;
    if (now - lastPlayTime < RATE_LIMIT_MS) {
      return null;
    }

    // Update last play time
    soundPlayTimes.current[soundName] = now;

    if (!window.audioElements || !window.audioElements[soundName]) {
      return null;
    }

    try {
      // Check if there's already an active promise for this sound
      if (activeSoundPromises.current[soundName]) {
        return null;
      }

      const audio = window.audioElements[soundName];

      // Set volume and loop
      audio.volume = VOLUMES[soundName] || 0.8;
      audio.loop = shouldLoop;

      // If this is a looping sound, stop other looping sounds
      if (shouldLoop) {
        Object.entries(window.audioElements).forEach(([key, otherAudio]) => {
          if (key !== soundName && otherAudio && otherAudio.loop) {
            try {
              otherAudio.pause();
              otherAudio.currentTime = 0;
            } catch (e) {
              // Ignore errors
            }
          }
        });
      }

      // Reset the audio if it was already playing
      try {
        if (!audio.paused) {
          audio.pause();
        }
        audio.currentTime = 0;
      } catch (e) {
        // Ignore errors - some browsers throw errors when setting currentTime
      }

      // Update playing sounds state
      setPlayingSounds(prev => ({
        ...prev,
        [soundName]: true
      }));

      // Create a promise to track playback
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        // Store the promise
        activeSoundPromises.current[soundName] = playPromise;

        playPromise
          .then(() => {
            // Clear the promise reference
            delete activeSoundPromises.current[soundName];
          })
          .catch(e => {
            // Clear the promise reference
            delete activeSoundPromises.current[soundName];

            // Handle autoplay restrictions
            if (e.name === 'NotAllowedError') {
              // Silent failure for autoplay restrictions
            } else {
              console.error(`Error playing sound ${soundName}:`, e);
            }

            // Update playing sounds state on error
            setPlayingSounds(prev => ({
              ...prev,
              [soundName]: false
            }));
          });
      }

      return audio;
    } catch (error) {
      // Clear any active promise
      delete activeSoundPromises.current[soundName];

      // Update playing status on error
      setPlayingSounds(prev => ({
        ...prev,
        [soundName]: false
      }));

      return null;
    }
  }, [isMuted]);

  // Function to play a sound with delay
  const playWithDelay = useCallback((soundName, delay = 0) => {
    if (isMuted) return Promise.resolve(null);

    return new Promise((resolve) => {
      setTimeout(() => {
        const audio = playSound(soundName);
        resolve(audio);
      }, delay);
    });
  }, [playSound, isMuted]);

  // Function to stop a specific sound
  const stopSound = useCallback((soundName) => {
    if (!window.audioElements || !window.audioElements[soundName]) {
      return;
    }

    try {
      const audio = window.audioElements[soundName];

      // Cancel any active play promise
      if (activeSoundPromises.current[soundName]) {
        delete activeSoundPromises.current[soundName];
      }

      if (!audio.paused) {
        audio.pause();
      }

      try {
        audio.currentTime = 0;
      } catch (e) {
        // Ignore errors - some browsers throw errors when setting currentTime
      }

      audio.loop = false; // Ensure loop is turned off

      // Update playing sounds state
      setPlayingSounds(prev => ({
        ...prev,
        [soundName]: false
      }));
    } catch (error) {
      console.error(`Error stopping sound ${soundName}:`, error);
    }
  }, []);

  // Function to play a sequence of sounds with specified delays
  const playSequence = useCallback((sequence) => {
    if (isMuted) return;

    if (!Array.isArray(sequence) || sequence.length === 0) {
      console.error('Invalid sound sequence');
      return;
    }

    try {
      let cumulativeDelay = 0;

      sequence.forEach(({ sound, delay = 0 }) => {
        if (!sound || !SOUNDS[sound]) return; // Skip invalid sounds

        cumulativeDelay += delay;
        playWithDelay(sound, cumulativeDelay);
      });
    } catch (error) {
      console.error('Error playing sound sequence:', error);
    }
  }, [playWithDelay, isMuted]);

  return (
    <SoundContext.Provider value={{
      isMuted,
      toggleMute,
      playSound,
      playWithDelay,
      stopSound,
      playSequence,
      playingSounds
    }}>
      {children}
    </SoundContext.Provider>
  );
};

export const useSound = () => useContext(SoundContext);

export default SoundContext;